import { useState, useMemo } from "react";
import { useRiderFavoriteDrivers } from "../../hooks/rider-operations/useRiderQueries";
import { useRiderStore } from "../../stores";
import Timer from "../shared/Timer";

function ChooseDriver({ userId, isAuth, onChooseDriver, onBack, prefillDriverIds, passengers }) {
  const hasPrefill = prefillDriverIds && prefillDriverIds.length > 0;
  const [selectedDriverIds, setSelectedDriverIds] = useState(hasPrefill ? prefillDriverIds : []);
  const [nextAvailableSelected, setNextAvailableSelected] = useState(!hasPrefill);

  // Fetch favorite drivers (populates the store)
  useRiderFavoriteDrivers(userId);
  const { favoriteDrivers } = useRiderStore();

  const availableFavoriteDrivers = useMemo(() => {
    return favoriteDrivers.filter((driver) => driver.isOnDuty);
  }, [favoriteDrivers]);

  const handleSelectNextAvailable = () => {
    setNextAvailableSelected(true);
    setSelectedDriverIds([]);
  };

  const handleSelectDriver = (driverId) => {
    setNextAvailableSelected(false);
    setSelectedDriverIds((prev) => {
      if (prev.includes(driverId)) {
        const updated = prev.filter((id) => id !== driverId);
        // If no drivers left, default back to Next Available
        if (updated.length === 0) {
          setNextAvailableSelected(true);
        }
        return updated;
      } else {
        return [...prev, driverId];
      }
    });
  };

  const handleChoose = () => {
    const driverIds = nextAvailableSelected ? [] : selectedDriverIds;
    onChooseDriver(driverIds);
  };

  // Calculate ETA from nextAvailableAt
  const calculateETA = (driverNextAvailableAt) => {
    if (!driverNextAvailableAt) return "Now";
    const now = new Date();
    const availableAt = new Date(driverNextAvailableAt);
    const diffMs = availableAt - now;
    if (diffMs <= 0) return "Now";
    const diffMins = Math.ceil(diffMs / 60000);
    if (diffMins < 60) return `~${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `~${hours}h ${mins}m`;
  };

  return (
    <div className="flex flex-col w-full">
      {/* Header */}
      <h3 className="text-white text-xl font-bold text-center mb-4">
        Choose Your Driver
      </h3>

      {/* Next Available Driver option */}
      <button
        type="button"
        className={`w-full p-4 rounded-lg border-2 text-left transition-all mb-4 ${
          nextAvailableSelected
            ? "bg-main-teal-dark/20 border-main-teal-light"
            : "bg-white/5 border-white/10 hover:border-white/30"
        }`}
        onClick={handleSelectNextAvailable}
      >
        <div className="flex items-center justify-between">
          <div>
            <p
              className={`font-semibold text-lg ${
                nextAvailableSelected ? "text-white" : "text-main-light-highlight"
              }`}
            >
              Next Available Driver
            </p>
            <p
              className={`text-sm ${
                nextAvailableSelected
                  ? "text-main-teal-light"
                  : "text-main-light-highlight/60"
              }`}
            >
              Any on-duty driver can accept your ride
            </p>
          </div>
          {nextAvailableSelected && (
            <div className="bg-main-teal-dark text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
              ✓
            </div>
          )}
        </div>
      </button>

      {/* Favorite Drivers Section */}
      {isAuth && availableFavoriteDrivers.length > 0 && (
        <>
          <p className="text-main-light-highlight/70 text-xs uppercase tracking-wide mb-2">
            Favorite Drivers Currently On Duty
          </p>

          <div className="max-h-60 overflow-y-auto space-y-2 mb-3 pr-1">
            {availableFavoriteDrivers.map((driver) => {
              const isSelected = selectedDriverIds.includes(driver.id);
              // Not enough free seats for this party — shown, but not pickable.
              const tooSmall =
                driver.seats != null && passengers > 0 && passengers > driver.seats;
              return (
                <button
                  key={driver.id}
                  type="button"
                  disabled={tooSmall}
                  title={tooSmall ? `${driver.name} only has ${driver.seats} free seat${driver.seats === 1 ? "" : "s"}` : undefined}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    tooSmall
                      ? "bg-white/5 border-white/10 opacity-50 cursor-not-allowed"
                      : isSelected
                        ? "bg-main-teal-dark/20 border-main-teal-light"
                        : "bg-white/5 border-white/10 hover:border-white/30"
                  }`}
                  onClick={() => !tooSmall && handleSelectDriver(driver.id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Driver initial avatar + name */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? "bg-main-teal-dark text-white"
                            : "bg-white/10 text-main-light-highlight"
                        }`}
                      >
                        <span className="font-semibold text-lg">
                          {driver.name?.charAt(0)?.toUpperCase() || "D"}
                        </span>
                      </div>
                      <p
                        className={`font-semibold truncate ${
                          isSelected ? "text-white" : "text-main-light-highlight"
                        }`}
                      >
                        {driver.name}
                      </p>
                    </div>

                    {/* Seats */}
                    <div className="text-center flex-shrink-0">
                      <p
                        className={`text-xs uppercase tracking-wide ${
                          isSelected
                            ? "text-main-teal-light"
                            : "text-main-light-highlight/60"
                        }`}
                      >
                        Seats
                      </p>
                      <p
                        className={`font-semibold text-sm ${
                          tooSmall
                            ? "text-main-red-light"
                            : isSelected
                              ? "text-white"
                              : "text-main-light-highlight"
                        }`}
                      >
                        {driver.seats ?? "—"}
                      </p>
                    </div>

                    {/* Queue */}
                    <div className="text-center flex-shrink-0">
                      <p
                        className={`text-xs uppercase tracking-wide ${
                          isSelected
                            ? "text-main-teal-light"
                            : "text-main-light-highlight/60"
                        }`}
                      >
                        Queue
                      </p>
                      <p
                        className={`font-semibold text-sm ${
                          isSelected ? "text-white" : "text-main-light-highlight"
                        }`}
                      >
                        {driver.riderQueue || 0}
                      </p>
                    </div>

                    {/* Available In */}
                    <div className="text-center flex-shrink-0">
                      <p
                        className={`text-xs uppercase tracking-wide ${
                          isSelected
                            ? "text-main-teal-light"
                            : "text-main-light-highlight/60"
                        }`}
                      >
                        Available In
                      </p>
                      <p
                        className={`font-semibold text-sm ${
                          isSelected ? "text-white" : "text-main-light-highlight"
                        }`}
                      >
                        {calculateETA(driver.driverNextAvailableAt)}
                      </p>
                    </div>

                    {/* Selected indicator - always reserves space */}
                    <div className="w-6 flex-shrink-0">
                      {isSelected && (
                        <div className="bg-main-teal-dark text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                          ✓
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* No favorites message for logged-in users */}
      {isAuth && availableFavoriteDrivers.length === 0 && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3 text-center">
          <p className="text-main-light-highlight/60 text-sm">
            None of your favorite drivers are currently on duty.
          </p>
        </div>
      )}

      {/* Not logged in message */}
      {!isAuth && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3 text-center">
          <p className="text-main-light-highlight/60 text-sm">
            Log in to choose from your favorite drivers.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col items-center gap-2 mt-1">
        <button
          type="button"
          className="btn btn-green btn-md"
          onClick={handleChoose}
        >
          Choose Driver
        </button>
        <button
          type="button"
          className="btn btn-red btn-md"
          onClick={onBack}
        >
          Back
        </button>
      </div>
    </div>
  );
}

export default ChooseDriver;
